/**
 * MultiSelectDropdown — reusable multi-select menu with checkmarks.
 *
 * Uses the existing dropdown primitives and keeps the menu open while
 * toggling options so users can pick multiple values quickly.
 */
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/cn";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  triggerLabel: string;
  noneSelectedLabel?: string;
  className?: string;
}

export function MultiSelectDropdown({
  options,
  selected,
  onChange,
  triggerLabel,
  noneSelectedLabel = "All",
  className,
}: MultiSelectDropdownProps) {
  const selectedSet = new Set(selected);

  function toggle(value: string) {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(Array.from(next));
  }

  const selectedLabels = options.flatMap((opt) =>
    selectedSet.has(opt.value) ? [opt.label] : [],
  );

  const summary =
    selectedLabels.length === 0
      ? noneSelectedLabel
      : selectedLabels.length <= 2
        ? selectedLabels.join(", ")
        : `${selectedLabels.length} selected`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="toolbar"
          size="md"
          className={cn("justify-between", className)}
        >
          <span className="truncate">
            {triggerLabel}: {summary}
          </span>
          <ChevronDown size={14} className="shrink-0 text-fg-mute" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[240px] text-fg">
        <DropdownMenuLabel>{triggerLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => {
          const active = selectedSet.has(opt.value);
          return (
            <DropdownMenuItem
              key={opt.value}
              onSelect={(e) => {
                e.preventDefault();
                toggle(opt.value);
              }}
              className="text-fg"
            >
              <span
                className={cn(
                  "inline-flex h-4 w-4 items-center justify-center rounded border border-line",
                  active && "border-violet/60 bg-violet/15",
                )}
              >
                {active ? <Check size={11} className="text-violet" /> : null}
              </span>
              {opt.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
